import { Injectable } from '@angular/core';
import {
  loadSciChart,
  SciChartJSDarkTheme,
  SciChartJSLightTheme,
  SciChartSurface,
} from '../charts/scichart';
import {
  PRINT_GRIDLINE_STYLE,
  PRINT_STROKE_OVERRIDES,
  PRINT_THEME_OVERRIDES,
  REPORT_AXIS_LABEL_PT,
  REPORT_AXIS_TITLE_PT,
  REPORT_BAND_STROKE_PT,
  REPORT_PLOT_BACKGROUND,
  isReportMode,
  reportFontPx,
  reportStrokePx,
} from './chart-theme';
import type { DomToImage } from 'dom-to-image';
import * as _domtoimage from 'dom-to-image';

// @types/dom-to-image declares a named `DomToImage` export that doesn't exist
// at runtime (the CJS module.exports IS the {toPng, …} object directly).
const domtoimage = _domtoimage as unknown as DomToImage;

/**
 * Bounds on the CAPTURE phase's awaits that could never settle.
 *
 * `PDF_REQUEST_TIMEOUT_MS` in the export component is real and correctly wired —
 * `.pipe(timeout(...))` on the generatePDF observable. It sits AFTER this phase,
 * and an rxjs timer only starts on subscribe, so when the stall is here it never
 * starts. A control guarding the wrong thing reads as adequate coverage in the
 * source and as no coverage in the failure.
 *
 * These bounds make the phase TERMINAL. They do not make it UNDERSTOOD: a
 * deadline firing tells you which await stalled, never why. Read a fired
 * deadline as the start of a diagnosis, not the end of one.
 */
const IMAGE_DECODE_TIMEOUT_MS = 10000;
const DOM_TO_IMAGE_TIMEOUT_MS = 20000;

/**
 * Waiting for an animation frame.
 *
 * requestAnimationFrame DOES NOT FIRE while the document is not being rendered.
 * There is no error and no rejection; the callback simply never runs, so the
 * promise wrapping it stays pending forever and no `try`/`catch` around it can
 * see anything.
 *
 * MEASURED, not inferred. On the shared authed browser on 2026-09-18 a page
 * reported `document.hidden = true`, `visibilityState = "hidden"` and **0 rAF
 * ticks in 25 seconds**, across five attempts to raise it. Chart updates in this
 * app are rAF-coalesced throughout, so that is not specific to the export.
 *
 * WHAT ACTUALLY CAUSES IT IS NARROWER THAN IT SOUNDS, and getting this wrong
 * sends people chasing the wrong trigger. Losing keyboard FOCUS does not do it:
 * a window that pops up in front and steals focus while leaving the page visible
 * changes nothing about rAF. What does it is the tab being hidden, the window
 * being FULLY occluded, or the window never being frontmost at all.
 *
 * HOW OFTEN A REAL OPERATOR HITS THIS IS UNMEASURED. An export takes 20-30s, so
 * starting one and switching away is plausible — but that is an assumption about
 * usage, not an observation, and it should not be repeated as though it were the
 * demonstrated cause of any particular hang.
 *
 * A frame is ~16 ms, so this is ~300x the normal case and still finite. When it
 * expires the capture proceeds against whatever is on the canvas, which may be
 * blank — and a blank capture is already handled: the chart is named and the
 * report refuses. A refusal beats a spinner that never resolves.
 */
const ANIMATION_FRAME_TIMEOUT_MS = 5000;

/**
 * Export capture at 2× scale for 300 DPI print quality.
 * A PDF content area is ~7.5 inches wide; 7.5 × 300 = 2250 px.
 * We render at 2400×1200 CSS px so SciChart's WebGL canvas is large enough,
 * then capture at 2× pixel ratio for a final 4800×2400 image.
 */
const EXPORT_CSS_WIDTH = 2400;
const EXPORT_CSS_HEIGHT = 1200;
const EXPORT_PIXEL_RATIO = 2;

/**
 * Per-chart capture geometry, because ASPECT RATIO IS PAGE LAYOUT.
 *
 * `EmbedChart` places every image with `.Image(bytes).FitWidth()`, so the PNG is
 * scaled to the 180 mm column and its HEIGHT follows from its own aspect ratio.
 * Nothing on the server decides how much of the page a chart gets — the shape of
 * the image does.
 *
 * At the default 2:1 that means 180 x 90 mm on a content column roughly 237 mm
 * tall: 38% of the page, which is Joel's "we have the whole page and are only
 * using a portion of it". For a waterfall — distance across, time down — the
 * wasted space is also the axis that carries the cure, so a taller capture buys
 * real time resolution rather than just filling the sheet.
 *
 * 1800 x 2000 prints as 180 x 200 mm, about 84% of the column, leaving the page
 * title and a margin of air. Captured at ratio 2 that is 3600 x 4000 px = 508 dpi
 * on paper, well past the 300 dpi the sizing below was chosen for, so the drop
 * from 2400 to 1800 CSS px costs nothing a reader can see.
 */
const EXPORT_GEOMETRY: Record<
  string,
  { cssWidth: number; cssHeight: number; ratio: number }
> = {
  heatmap3D: { cssWidth: 1800, cssHeight: 2000, ratio: EXPORT_PIXEL_RATIO },
};

/**
 * Width of the temperature colour scale drawn down the right of a heatmap
 * capture, as a fraction of the image.
 */
const COLOR_SCALE_WIDTH_FRACTION = 0.14;

/** Matches the chart background so a partial frame does not show white. */
const CAPTURE_BACKGROUND = '#1d1d1d';

/**
 * The report is a LIGHT document; the app is a dark console.
 *
 * The desktop's report renders its plots light, and a PDF is printed and read on
 * paper far more often than a dashboard is. Dark chart panels in a light report
 * look like errors and waste toner, so the capture re-themes each surface for the
 * duration of the grab and puts it back afterwards.
 */
// LAZY, and that is load-bearing. These used to be constructed at module scope,
// which runs when Angular instantiates this root-provided service at bootstrap —
// before any component has awaited loadSciChart(), so the constructors would not
// exist yet. Both are only ever needed from inside a capture, which happens long
// after a surface has been created.
let reportThemeInstance: SciChartJSLightTheme | null = null;
function reportTheme(): SciChartJSLightTheme {
  return (reportThemeInstance ??= Object.assign(
    new SciChartJSLightTheme(),
    PRINT_THEME_OVERRIDES
  ));
}

let appThemeInstance: SciChartJSDarkTheme | null = null;
function appTheme(): SciChartJSDarkTheme {
  return (appThemeInstance ??= new SciChartJSDarkTheme());
}
const REPORT_BACKGROUND = REPORT_PLOT_BACKGROUND;

/**
 * A temperature colour scale to draw beside a captured heatmap.
 *
 * Supplied by the component that owns the chart, because the domain is in the
 * USER'S units and only the component knows which those are — the scale is
 * -10..180 C or 14..356 F for the same gradient.
 */
export interface ColorScaleSpec {
  title: string;
  min: number;
  max: number;
  unitSuffix: string;
  stops: ReadonlyArray<{ offset: number; color: string }>;
}

/**
 * Why a requested chart produced no image.
 *
 * - `absent`       — no surface registered, or its div is not in the DOM. The
 *                    chart does not exist for this project. NOT a fault.
 * - `no-data`      — a surface is there but nothing is bound to it.
 * - `blank-render` — it drew, and what came back was a flat field.
 *
 * The caller words its message from this, because "failed to render" is wrong
 * and misleading for `absent`.
 */
export type CaptureFailureKind = 'absent' | 'no-data' | 'blank-render';

@Injectable({ providedIn: 'root' })
export class ChartCaptureService {
  private divIds = new Map<string, string>();
  private renderHandlers = new Map<string, () => void>();
  private surfaces = new Map<string, SciChartSurface>();
  /** Chart name → colour scale to draw into the report capture's right margin. */
  private colorScales = new Map<string, () => ColorScaleSpec | null>();
  /** Live registrations displaced by an offscreen report render, to be restored. */
  private displaced = new Map<
    string,
    { surface: SciChartSurface; divId: string; handler?: () => void }
  >();
  private resizing = false;
  /** True while a surface is light-themed for an export capture. */
  private reportTheme = false;

  register(name: string, surface: SciChartSurface, divId: string): void {
    // A REPORT REGISTRATION MUST NOT DISPLACE THE LIVE TAB WITHOUT RECORDING IT.
    //
    // The export prerenders every report chart offscreen under THE SAME NAMES the
    // live tabs use, so an offscreen registration takes the name from the live
    // surface. Remember the one it displaced, so unregister() can give the name
    // back when the offscreen component is torn down.
    const previousSurface = this.surfaces.get(name);
    const previousDivId = this.divIds.get(name);
    if (isReportMode() && previousSurface && previousSurface !== surface && previousDivId) {
      this.displaced.set(name, {
        surface: previousSurface,
        divId: previousDivId,
        handler: this.renderHandlers.get(name),
      });
    }

    this.divIds.set(name, divId);
    this.surfaces.set(name, surface);
    console.log(`ChartCapture: registered "${name}" → #${divId}`);
  }

  /**
   * Declare the colour scale that belongs beside this chart in the report.
   *
   * A THUNK, not a value: the domain is in display units, and the unit can change
   * under a chart that was registered long before. Reading it at capture time is
   * the only way the printed scale and the printed heatmap are guaranteed to be
   * describing the same numbers.
   */
  registerColorScale(name: string, spec: () => ColorScaleSpec | null): void {
    this.colorScales.set(name, spec);
  }

  unregister(name: string, surface: SciChartSurface): void {
    const handler = this.renderHandlers.get(name);
    if (handler) {
      try {
        surface.rendered.unsubscribe(handler);
      } catch {
        // already torn down
      }
    }

    // ONLY clear the entry if it is still THIS surface's.
    //
    // Report charts are rendered offscreen under the same names the live tabs
    // use, so an offscreen component being destroyed would otherwise wipe the
    // live tab's registration and leave the app unable to capture that chart for
    // the rest of the session. Identity, not name, decides.
    if (this.surfaces.get(name) !== surface) {
      return;
    }
    this.renderHandlers.delete(name);
    this.divIds.delete(name);
    this.surfaces.delete(name);

    // GIVE THE LIVE TAB ITS REGISTRATION BACK.
    //
    // Identity-matching stops an offscreen teardown wiping a DIFFERENT surface's
    // entry, but when the offscreen surface IS the current entry — which it is,
    // having displaced the live one moments earlier — removing it left the name
    // unregistered while the live chart was still on screen. After generating one
    // report the screenshot button then refused a visibly-rendering chart with
    // "no data to capture", because nothing was registered under that name.
    const previous = this.displaced.get(name);
    if (previous) {
      this.displaced.delete(name);
      this.surfaces.set(name, previous.surface);
      this.divIds.set(name, previous.divId);
      if (previous.handler) {
        this.renderHandlers.set(name, previous.handler);
      }
      console.log(`ChartCapture: restored live registration for "${name}"`);
    }
  }

  /** True if a live surface is currently registered under this name. */
  has(name: string): boolean {
    return this.surfaces.has(name) && this.divIds.has(name);
  }

  /**
   * True if the surface actually holds something to draw.
   *
   * REGISTRATION IS NOT DATA. register() runs immediately after
   * SciChartSurface.create(), before any series is bound — and an empty surface
   * renders perfectly happily, just with nothing on it. That is the whole blank-
   * chart bug: the export waited for registration, captured a valid PNG of an
   * empty chart, and since the PNG was non-null it never fell back to the cached
   * snapshot from when the tab was last visible.
   */
  hasRenderedData(name: string): boolean {
    const surface = this.surfaces.get(name);
    if (!surface) {
      return false;
    }
    try {
      const series = surface.renderableSeries;
      for (let i = 0; i < series.size(); i++) {
        const ds = series.get(i)?.dataSeries as
          | { count?: () => number; arrayWidth?: number; arrayHeight?: number }
          | undefined;
        if (!ds) {
          continue;
        }
        // XyDataSeries and friends count points; heatmaps expose a grid instead.
        if (typeof ds.count === 'function' && ds.count() > 0) {
          return true;
        }
        if ((ds.arrayWidth ?? 0) > 0 && (ds.arrayHeight ?? 0) > 0) {
          return true;
        }
      }
    } catch {
      // A disposed or half-built surface must read as "no data", not throw.
      return false;
    }
    return false;
  }

  /**
   * Capture ONE live chart exactly as it appears on screen — the screenshot button.
   *
   * SEPARATE FROM BOTH EXISTING ENTRY POINTS, deliberately:
   *
   * - NOT `captureAllForExport()`. That is the REPORT path: it moves the node
   *   off-screen, resizes it to 2400x1200 and re-themes it light for print. On a
   *   button click the user would watch their chart vanish, flash white and come
   *   back, and would receive a print-themed image that does not match what they
   *   were looking at.
   *
   * So this reads the live surface in place, at its ON-SCREEN size, in the app's
   * OWN theme, through the composite reader that is safe against the WebGL buffer
   * being cleared. Nothing moves and nothing re-themes, so there is no visible
   * disturbance at all.
   *
   * TIMEOUT IS SHORT ON PURPOSE. The export path waits 15s because a missing
   * report chart is worse than a slow one. Here a person is waiting on a click, so
   * it fails fast and the caller says so — a hung button is worse than one that
   * reports it could not capture.
   *
   * Returns null rather than a blank image: `hasRenderedData` refuses an empty
   * surface up front, and `isBlank` refuses one that composited to a flat field.
   */
  async captureVisible(name: string, timeoutMs = 4000): Promise<string | null> {
    const surface = this.surfaces.get(name);
    const divId = this.divIds.get(name);
    const node = divId ? document.getElementById(divId) : null;
    if (!surface || !node || !this.hasRenderedData(name)) {
      return null;
    }

    // The size it is actually drawn at. clientWidth/Height rather than the export
    // constants, because the point of this button is "what I am looking at".
    const width = node.clientWidth || node.offsetWidth;
    const height = node.clientHeight || node.offsetHeight;
    if (width <= 0 || height <= 0) {
      return null;
    }

    try {
      const composited = await this.captureSurfaceComposite(
        surface, width, height, EXPORT_PIXEL_RATIO, timeoutMs
      );
      if (composited && !(await this.isBlank(composited))) {
        return composited;
      }

      // FALLBACK TO dom-to-image, exactly as the export path does.
      //
      // The composite reads `surface.domCanvasWebGL` for the trace and
      // `domCanvas2D` for the axes. MEASURED 2026-09-15 against a real surface:
      // `domCanvasWebGL` can be **undefined** while `domCanvas2D` is a healthy
      // 640x320 and the surface reports a render — under software WebGL at least.
      // When that happens the composite contains axes and gridlines but NO
      // TRACE, isBlank correctly rejects it, and without this branch the button
      // returns nothing on a chart that is plainly visible on screen.
      //
      // Whether a hardware-GL browser can also land there is NOT established, so
      // this is insurance rather than a fix for a confirmed production case. It
      // costs one extra attempt only when the fast path has already failed, and
      // the export path has carried the same fallback all along — leaving the
      // interactive path as the only single-route capture was the asymmetry.
      if (composited) {
        console.warn(
          `ChartCapture: composite for "${name}" was blank — trying dom-to-image`
        );
      }
      const fallback = await this.captureNodeHiRes(node, width, height, EXPORT_PIXEL_RATIO);
      if (!fallback || (await this.isBlank(fallback))) {
        // STILL nothing rather than a blank PNG. The caller reports "could not
        // capture", which is the honest outcome — a valid image of nothing is
        // the failure this whole path exists to avoid.
        return null;
      }
      return fallback;
    } catch (err) {
      console.warn(`ChartCapture: interactive capture failed for "${name}"`, err);
      return null;
    }
  }

  /** Capture names that yielded no image on the last export. */
  lastMissing: string[] = [];

  /**
   * Why each name in `lastMissing` failed, so the operator is told which chart
   * and what went wrong rather than just that "something" did. Kept separate
   * from lastMissing so existing callers that only need the names are unchanged.
   *
   * The DISTINCTION THAT MATTERS is 'absent' versus the other two. A chart this
   * project never had is a user-correctable situation; a chart that exists and
   * would not draw is a fault. Describing the first as "failed to render" sends
   * an operator hunting a bug that is not there.
   */
  lastFailures: Record<string, CaptureFailureKind> = {};

  /**
   * Capture all requested charts at print quality for PDF export.
   * Charts still in the DOM are resized to a large off-screen container
   * and captured at 2× pixel ratio. Otherwise the cached snapshot is used.
   */
  async captureAllForExport(names: string[]): Promise<Record<string, string>> {
    const result: Record<string, string> = {};

    this.lastMissing = [];
    this.lastFailures = {};

    for (const name of names) {
      const divId = this.divIds.get(name);
      const surface = this.surfaces.get(name);
      const node = divId ? document.getElementById(divId) : null;

      // CLASSIFY AT THE BRANCH, NOT AFTERWARDS.
      //
      // An earlier version of this re-derived the reason once the capture had
      // already failed, by asking `has(name)` again — which cannot see the
      // difference between "no surface registered" and "surface registered but
      // its div is not in the DOM", and so reported the second as the third.
      // The reason has to be recorded where the decision is actually made.
      let failure: CaptureFailureKind;

      // Only capture a surface that has something on it. Capturing an empty one
      // produces a valid, blank PNG, which used to mask a perfectly good cached
      // snapshot — which is exactly how charts came out blank unless their tab
      // had been visited.
      if (!surface || !node) {
        // ABSENT: nothing was ever built for this chart in this project. Not a
        // rendering failure, and the caller must not describe it as one.
        failure = 'absent';
      } else if (!this.hasRenderedData(name)) {
        failure = 'no-data';
      } else {
        const geo = EXPORT_GEOMETRY[name] ?? {
          cssWidth: EXPORT_CSS_WIDTH,
          cssHeight: EXPORT_CSS_HEIGHT,
          ratio: EXPORT_PIXEL_RATIO,
        };
        const b64 = await this.captureAtSize(
          name, node, surface, geo.cssWidth, geo.cssHeight, geo.ratio
        );
        if (b64) {
          result[name] = b64;
          continue;
        }
        failure = 'blank-render';
      }

      // NO SUBSTITUTION. A chart that could not be captured is REPORTED, never
      // replaced by the cached on-screen snapshot.
      //
      // What used to be here: `result[name] = this.snapshots.get(name)`. That
      // rescued a failed capture with a picture taken at ON-SCREEN size, from
      // whenever the tab was last live — so a report generated against a broken
      // chart came back carrying a plausible, stale-but-correct image while the
      // screen itself was blank. The report is the artefact customers actually
      // read, which made it the one least able to reveal a regression; the only
      // tell was the image's SIZE (cache ~900px, export 4800px) or the on-screen
      // legend falling back to "Series 1/2/3".
      //
      // A wrong report is worse than no report, so this now fails the way
      // `captureVisible` (the screenshot button) already did: it returns nothing
      // and says which chart. That button and this path run the SAME two gates —
      // `hasRenderedData` above and `isBlank` inside captureAtSize — and the
      // divergence in what they did afterwards was the defect, not the policy.
      //
      // The caller REFUSES TO BUILD THE PDF when this list is non-empty; it does
      // not ship a report with holes in it.
      this.lastMissing.push(name);
      this.lastFailures[name] = failure;
      console.warn(
        `ChartCapture: NO IMAGE for "${name}" — surface registered=${this.has(name)}, ` +
          `hasData=${this.hasRenderedData(name)}, reason=${failure}`
      );
    }

    console.log(
      `ChartCapture: captureAllForExport returning ${Object.keys(result).length}/${names.length} images`,
      Object.keys(result)
    );
    return result;
  }

  /**
   * Resize → re-render → capture at high resolution → restore.
   *
   * The trick for pixel-ratio scaling: we render the SciChart canvas at
   * cssWidth × cssHeight CSS pixels, then draw that into a canvas that is
   * (cssWidth × ratio) × (cssHeight × ratio) physical pixels. This gives
   * us a sharp, print-ready image without relying on devicePixelRatio.
   */
  private async captureAtSize(
    name: string,
    node: HTMLElement,
    surface: SciChartSurface,
    cssWidth: number,
    cssHeight: number,
    ratio: number
  ): Promise<string | null> {
    const orig = {
      width: node.style.width,
      height: node.style.height,
      flex: node.style.flex,
      position: node.style.position,
      zIndex: node.style.zIndex,
      overflow: node.style.overflow,
      left: node.style.left,
      top: node.style.top,
      visibility: node.style.visibility,
    };

    let themed = false;
    let restoreStrokes: (() => void) | null = null;
    let restoreSizing: (() => void) | null = null;
    try {
      this.resizing = true;

      // Light theme for the report. Applied before the resize so the re-theme
      // and the resize settle in the same layout pass rather than costing two.
      try {
        surface.applyTheme(reportTheme());
        themed = true;
        this.reportTheme = true;
        restoreStrokes = this.applyPrintStrokes(surface);
        // AFTER the theme, never before: applyTheme rewrites tick label colour
        // and would be free to rewrite the label style wholesale with it.
        restoreSizing = this.applyPrintSizing(surface, cssWidth);
      } catch (err) {
        console.warn(`ChartCapture: could not light-theme "${name}"`, err);
      }

      // Move off-screen and set to export dimensions. visibility must be forced
      // back to visible: while the export panel is open the route content is
      // hidden via visibility (see main-layout .export-hidden), and dom-to-image
      // copies the computed style — an inherited 'hidden' captures a blank PNG.
      node.style.position = 'fixed';
      node.style.left = '-99999px';
      node.style.top = '0px';
      node.style.zIndex = '-9999';
      node.style.overflow = 'hidden';
      node.style.flex = 'none';
      node.style.visibility = 'visible';
      node.style.width = `${cssWidth}px`;
      node.style.height = `${cssHeight}px`;

      // Let the resize settle into layout, then give the renderer real animation
      // frames to draw into. An offscreen surface does still rasterise — it is
      // laid out, and is neither display:none nor visibility:hidden — but it
      // draws on SciChart's own loop, so reading on the very next tick can beat
      // the first frame.
      await this.withDeadline(
        new Promise<void>((r) => requestAnimationFrame(() => setTimeout(r, 80))),
        ANIMATION_FRAME_TIMEOUT_MS,
        'layout-settle animation frame',
        undefined
      );
      surface.invalidateElement();
      await this.withDeadline(
        new Promise<void>((r) =>
          requestAnimationFrame(() => requestAnimationFrame(() => r()))
        ),
        ANIMATION_FRAME_TIMEOUT_MS,
        'post-invalidate animation frame',
        undefined
      );

      // PRIMARY: read the surface's own canvases inside its render callback.
      // This is the path that does not depend on the WebGL drawing buffer
      // surviving compositing, which it does not.
      const composited = await this.captureSurfaceComposite(
        surface, cssWidth, cssHeight, ratio, 15000, name
      );
      if (composited && !(await this.isBlank(composited))) {
        return composited;
      }
      if (composited) {
        console.warn(
          `ChartCapture: composite for "${name}" came back blank — trying dom-to-image`
        );
      }

      // FALLBACK: the original dom-to-image path. Kept because it also picks up
      // any non-canvas chrome inside the chart div, and because a second attempt
      // costs far less than a blank page in the report.
      const b64 = await this.captureNodeHiRes(node, cssWidth, cssHeight, ratio);
      if (b64 && (await this.isBlank(b64))) {
        console.warn(`ChartCapture: dom-to-image for "${name}" is blank too`);
        return null;
      }
      return b64;
    } catch (err) {
      console.warn(`ChartCapture: high-res capture failed for "${name}"`, err);
      return null;
    } finally {
      node.style.width = orig.width;
      node.style.height = orig.height;
      node.style.flex = orig.flex;
      node.style.position = orig.position;
      node.style.left = orig.left;
      node.style.top = orig.top;
      node.style.zIndex = orig.zIndex;
      node.style.overflow = orig.overflow;
      node.style.visibility = orig.visibility;
      if (restoreSizing) {
        try {
          restoreSizing();
        } catch (err) {
          console.warn(`ChartCapture: could not restore sizing for "${name}"`, err);
        }
      }
      if (restoreStrokes) {
        // Strokes first, then the theme — both must come back even if the
        // capture threw, or the next tab the user opens is wearing print colours.
        try {
          restoreStrokes();
        } catch (err) {
          console.warn(`ChartCapture: could not restore strokes for "${name}"`, err);
        }
      }
      if (themed) {
        // Back to the app's own theme, always — a surface left light after an
        // export would be a visible bug on the next tab the user opens.
        try {
          surface.applyTheme(appTheme());
        } catch (err) {
          console.warn(`ChartCapture: could not restore theme for "${name}"`, err);
        }
        this.reportTheme = false;
      }
      surface.invalidateElement();
      this.resizing = false;
    }
  }

  /**
   * Swap series strokes that are unreadable on white, returning an undo.
   *
   * Keyed by the app colour rather than by series name: names drift, and a
   * series added later with the same white stroke gets the fix for free instead
   * of silently printing blank.
   */
  private applyPrintStrokes(surface: SciChartSurface): () => void {
    const map = new Map(
      PRINT_STROKE_OVERRIDES.map(([from, to]) => [from.toLowerCase(), to])
    );
    const undo: Array<() => void> = [];
    try {
      const series = surface.renderableSeries;
      for (let i = 0; i < series.size(); i++) {
        const rs = series.get(i) as { stroke?: string } | undefined;
        const current = rs?.stroke;
        if (!rs || typeof current !== 'string') {
          continue;
        }
        const replacement = map.get(current.toLowerCase());
        if (!replacement) {
          continue;
        }
        rs.stroke = replacement;
        undo.push(() => {
          rs.stroke = current;
        });
      }
    } catch (err) {
      console.warn('ChartCapture: print stroke swap failed', err);
    }
    return () => undo.forEach((fn) => fn());
  }

  /**
   * Resize axis text, gridlines and stroke weights for a printed page.
   *
   * WHY THIS IS NOT A THEME. A SciChart theme carries COLOURS, not sizes, and
   * where it does carry a size — `majorGridLineBrush`, say — it only reaches an
   * axis that never set its own. Every axis in this app sets
   * `majorGridLineStyle: GRIDLINE_STYLE.major`, which is WHITE for the console,
   * so the light theme left the report's gridlines white on white: not faint,
   * absent. There is no theme key at all for label font size.
   *
   * WHY IT IS KEYED ON cssWidth AND NOT ON A FIXED NUMBER. The image is fitted to
   * a 180 mm column whatever its pixel size, so what a reader sees depends only
   * on a feature's ratio to the image width. A 13 px label on a 2400 px capture
   * prints at about 3 pt. The same 13 px on the 1800 px heatmap capture would
   * print at 4 pt — different, for no reason anyone chose. Deriving from the
   * capture width makes both land on REPORT_AXIS_LABEL_PT.
   *
   * Everything is captured as it was and handed back as an undo, because these
   * are the LIVE surfaces: a chart left wearing print sizes is a visible bug on
   * the next tab the user opens, and this runs on a path that can throw.
   */
  private applyPrintSizing(surface: SciChartSurface, cssWidth: number): () => void {
    const undo: Array<() => void> = [];
    const labelPx = reportFontPx(REPORT_AXIS_LABEL_PT, cssWidth);
    const titlePx = reportFontPx(REPORT_AXIS_TITLE_PT, cssWidth);

    const sizeAxis = (axis: Record<string, unknown> | undefined): void => {
      if (!axis) {
        return;
      }
      // labelStyle and axisTitleStyle are plain objects on the axis; SciChart
      // re-reads them on the next draw, so assigning a NEW object (rather than
      // mutating the existing one) is what makes the change take.
      for (const [key, px] of [
        ['labelStyle', labelPx],
        ['axisTitleStyle', titlePx],
      ] as const) {
        const current = axis[key] as Record<string, unknown> | undefined;
        if (!current) {
          continue;
        }
        const previous = { ...current };
        axis[key] = { ...current, fontSize: px };
        undo.push(() => {
          axis[key] = previous;
        });
      }

      for (const [key, style] of [
        ['majorGridLineStyle', PRINT_GRIDLINE_STYLE.major],
        ['minorGridLineStyle', PRINT_GRIDLINE_STYLE.minor],
      ] as const) {
        const current = axis[key] as
          | { color?: string; strokeThickness?: number }
          | undefined;
        if (!current) {
          continue;
        }
        const previous = { ...current };
        axis[key] = {
          ...current,
          color: style.color,
          strokeThickness: reportStrokePx(
            current.strokeThickness ?? style.strokeThickness,
            cssWidth
          ),
        };
        undo.push(() => {
          axis[key] = previous;
        });
      }
    };

    try {
      for (const collection of [surface.xAxes, surface.yAxes]) {
        for (let i = 0; i < collection.size(); i++) {
          sizeAxis(collection.get(i) as unknown as Record<string, unknown>);
        }
      }

      // Trace weights. A 2 px line on a 2400 px capture is a 0.43 pt hairline on
      // paper — the other half of "use higher contrast colors", because a red
      // that thin reads as pink whatever its hex says.
      //
      // A FILLED series is the exception and gets an absolute weight rather than
      // the relative one. `fill` is what distinguishes a time-over band from a
      // trace, and a band's outline is its identity on paper — see
      // REPORT_BAND_STROKE_PT. Keeping the relative rule would leave a 1 px band
      // edge printing at half a point, which is why five nested bands read as one
      // wash however their fills are coloured.
      const series = surface.renderableSeries;
      for (let i = 0; i < series.size(); i++) {
        const rs = series.get(i) as
          | { strokeThickness?: number; fill?: string }
          | undefined;
        const current = rs?.strokeThickness;
        if (!rs || typeof current !== 'number' || current <= 0) {
          continue;
        }
        rs.strokeThickness = rs.fill
          ? Math.max(current, reportFontPx(REPORT_BAND_STROKE_PT, cssWidth))
          : reportStrokePx(current, cssWidth);
        undo.push(() => {
          rs.strokeThickness = current;
        });
      }
    } catch (err) {
      console.warn('ChartCapture: print sizing failed', err);
    }

    return () => undo.forEach((fn) => fn());
  }

  /**
   * The temperature colour scale for a heatmap capture — Rob: "there's no X
   * temperature gauge over here for colors."
   *
   * WHY IT IS DRAWN HERE RATHER THAN CAPTURED. The scale beside the waterfall on
   * screen is a SEPARATE SciChart surface in a SEPARATE div — HeatmapLegend
   * creates `#temp-legend` next to `#waterfall-graph` — and the capture reads the
   * canvases of the ONE surface registered under the chart's name. The scale was
   * never in the image and no amount of resizing that div would have put it
   * there.
   *
   * Compositing the second surface instead was the obvious alternative and is
   * worse: its WebGL buffer has to be read inside ITS OWN `rendered` callback
   * (SciChart does not preserve the drawing buffer), which cannot be nested
   * inside the main surface's, so the two would race. And the on-screen scale is
   * built for a dark console — '#FFFFFF77' border, 'White' tick lines, tick
   * labels switched off — so it would arrive invisible on a white page and need
   * re-theming anyway.
   *
   * Drawing it is deterministic, costs no second render, and gets the printed
   * font size for free. The gradient comes from ZONE_GRADIENT_STOPS, which the
   * heatmap series itself now reads, so the scale cannot describe a ramp the
   * chart is not using.
   */
  private drawColorScale(
    ctx: CanvasRenderingContext2D,
    spec: ColorScaleSpec,
    x: number,
    y: number,
    width: number,
    height: number,
    cssWidth: number,
    ratio: number
  ): void {
    const fontPx = reportFontPx(REPORT_AXIS_LABEL_PT, cssWidth) * ratio;
    const titlePx = reportFontPx(REPORT_AXIS_TITLE_PT, cssWidth) * ratio;
    const pad = Math.round(fontPx * 0.6);

    // The bar occupies the left third of the strip; the labels sit to its right,
    // which is where a reader coming off the chart looks first.
    const barW = Math.round(width * 0.3);
    const barX = x + pad;
    const barTop = y + pad + titlePx * 1.6;
    const barH = height - (barTop - y) - pad * 2;
    if (barW <= 0 || barH <= 0) {
      return;
    }

    // Offset 0 is the BOTTOM of the bar: the scale reads cold at the foot and hot
    // at the head, the way a thermometer does and the way the on-screen legend
    // is oriented.
    const gradient = ctx.createLinearGradient(0, barTop + barH, 0, barTop);
    for (const stop of spec.stops) {
      gradient.addColorStop(Math.min(1, Math.max(0, stop.offset)), stop.color);
    }
    ctx.fillStyle = gradient;
    ctx.fillRect(barX, barTop, barW, barH);
    ctx.strokeStyle = '#1A1A1A';
    ctx.lineWidth = Math.max(1, Math.round(ratio));
    ctx.strokeRect(barX, barTop, barW, barH);

    ctx.fillStyle = '#1A1A1A';
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';

    // TWO KINDS OF TICK, and both are needed.
    //
    // The gradient's own STOPS are where the colour starts meaning something
    // else — on the waterfall they are the zone boundaries, the temperature at
    // which a reading stops being "heating" and starts being "curing". Those are
    // the numbers a reader actually wants off this scale, and they fall at 27 and
    // 64 C, nowhere near a round number.
    //
    // But stops alone left a single unbroken ramp from 64 to 180 with nothing to
    // read against, so ROUND VALUES go in as well, on a 1/2/5 x 10^n step aiming
    // for about eight labels. Anything landing within half a step of a stop is
    // dropped rather than printed on top of it.
    const span = Math.abs(spec.max - spec.min) || 1;
    const rawStep = span / 8;
    const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
    const step =
      (rawStep / mag >= 5 ? 5 : rawStep / mag >= 2 ? 2 : 1) * mag;

    const offsets = new Set<number>([0, 1]);
    for (const stop of spec.stops) {
      offsets.add(Math.min(1, Math.max(0, stop.offset)));
    }
    const stopValues = [...offsets].map(
      (o) => spec.min + o * (spec.max - spec.min)
    );
    for (
      let v = Math.ceil(spec.min / step) * step;
      v <= spec.max;
      v += step
    ) {
      if (stopValues.some((sv) => Math.abs(sv - v) < step * 0.5)) {
        continue;
      }
      offsets.add((v - spec.min) / (spec.max - spec.min));
    }
    ctx.font = `${fontPx}px sans-serif`;
    const tickLen = Math.round(barW * 0.25);
    for (const offset of [...offsets].sort((a, b) => a - b)) {
      const ty = barTop + barH - offset * barH;
      ctx.beginPath();
      ctx.moveTo(barX + barW, ty);
      ctx.lineTo(barX + barW + tickLen, ty);
      ctx.stroke();
      const value = spec.min + offset * (spec.max - spec.min);
      ctx.fillText(
        `${Math.round(value)}${spec.unitSuffix}`,
        barX + barW + tickLen + pad * 0.5,
        ty
      );
    }

    ctx.font = `bold ${titlePx}px sans-serif`;
    ctx.textAlign = 'left';
    ctx.fillText(spec.title, x + pad, y + pad + titlePx * 0.6);
  }

  /**
   * Read the surface's own canvases, INSIDE the render callback.
   *
   * WHY THIS EXISTS. SciChart creates its WebGL context without
   * `preserveDrawingBuffer`, so the drawing buffer is cleared once the browser
   * composites the frame. canvas.toDataURL() after that point — which is what
   * dom-to-image does when it clones a <canvas> — returns an EMPTY image. The
   * capture therefore depended on landing in the same frame as the draw: it
   * worked often enough on a visible, continuously-redrawing chart to look
   * correct, and produced a solid dark rectangle whenever it lost the race.
   *
   * Reading the WebGL canvas synchronously inside `rendered` is the supported
   * way to do this: at that instant the buffer still holds the frame. The 2D
   * overlay (axes, labels, gridlines) is an ordinary canvas and survives, so it
   * is drawn over the top, then the SVG annotation layer, which needs an async
   * image decode and is safe to do afterwards because the SVG DOM persists.
   *
   * Subscribing BEFORE invalidateElement() also fixes the timeout: the previous
   * order subscribed after, so a render that completed promptly fired before
   * anyone was listening, the wait sat out its full 5 s, and the capture
   * then ran against whatever was on the canvas — usually nothing.
   */
  private captureSurfaceComposite(
    surface: SciChartSurface,
    cssWidth: number,
    cssHeight: number,
    ratio: number,
    timeoutMs = 15000,
    name?: string
  ): Promise<string | null> {
    return new Promise((resolve) => {
      const target = document.createElement('canvas');
      target.width = Math.round(cssWidth * ratio);
      target.height = Math.round(cssHeight * ratio);
      const ctx = target.getContext('2d');
      if (!ctx) {
        resolve(null);
        return;
      }

      // Resolved BEFORE the render callback: everything inside that callback runs
      // synchronously while the WebGL buffer is still intact, and a thunk that
      // throws in there would cost the whole capture.
      let scale: ColorScaleSpec | null = null;
      if (name && this.reportTheme) {
        try {
          scale = this.colorScales.get(name)?.() ?? null;
        } catch (err) {
          console.warn(`ChartCapture: colour scale for "${name}" failed`, err);
        }
      }

      let done = false;
      let timer: ReturnType<typeof setTimeout>;

      const onRendered = () => {
        if (done) {
          return;
        }
        done = true;
        clearTimeout(timer);
        try {
          surface.rendered.unsubscribe(onRendered);
        } catch {
          // a surface torn down mid-capture must not reject the promise
        }

        // EVERYTHING in this block runs synchronously, while the WebGL buffer
        // is still intact. Nothing may await before the drawImage below.
        try {
          ctx.fillStyle = this.reportTheme ? REPORT_BACKGROUND : CAPTURE_BACKGROUND;
          ctx.fillRect(0, 0, target.width, target.height);

          // The chart takes the whole frame unless a colour scale is claiming the
          // right-hand margin, in which case it is squeezed rather than cropped —
          // a crop would lose the last of the liner, which is data.
          const plotW = scale
            ? Math.round(target.width * (1 - COLOR_SCALE_WIDTH_FRACTION))
            : target.width;

          const gl = surface.domCanvasWebGL;
          if (gl && gl.width > 0 && gl.height > 0) {
            ctx.drawImage(gl, 0, 0, plotW, target.height);
          }
          const overlay = surface.domCanvas2D;
          if (overlay && overlay.width > 0 && overlay.height > 0) {
            ctx.drawImage(overlay, 0, 0, plotW, target.height);
          }
          if (scale) {
            this.drawColorScale(
              ctx, scale, plotW, 0, target.width - plotW, target.height,
              cssWidth, ratio
            );
          }
        } catch (err) {
          console.warn('ChartCapture: canvas composite failed', err);
          resolve(null);
          return;
        }

        // Annotation layer last. Async is fine here — the SVG is live DOM, not a
        // buffer that gets cleared.
        // BOUNDED, because this runs AFTER clearTimeout(timer) above — the 15s
        // composite bound has already been released by the time control reaches
        // here, so an SVG rasterise that never settles would hang the capture
        // with no timer left to catch it. The annotation layer is decoration; a
        // chart without it beats an export that never returns.
        this.withDeadline(
          this.drawSvgLayer(ctx, surface, target.width, target.height),
          IMAGE_DECODE_TIMEOUT_MS,
          'annotation layer rasterise',
          undefined
        )
          .catch(() => undefined)
          .then(() => {
            const url = target.toDataURL('image/png');
            resolve(url.split(',')[1] || null);
          });
      };

      try {
        surface.rendered.subscribe(onRendered);
      } catch {
        resolve(null);
        return;
      }

      timer = setTimeout(() => {
        if (done) {
          return;
        }
        done = true;
        try {
          surface.rendered.unsubscribe(onRendered);
        } catch {
          // ignore
        }
        console.warn(
          'ChartCapture: surface never reported a render within ' +
            `${timeoutMs}ms — no image rather than a blank one`
        );
        resolve(null);
      }, timeoutMs);

      // Now ask for the frame we just subscribed to.
      surface.invalidateElement();
    });
  }

  /** Rasterise the SVG annotation layer over the composited bitmap. */
  private drawSvgLayer(
    ctx: CanvasRenderingContext2D,
    surface: SciChartSurface,
    width: number,
    height: number
  ): Promise<void> {
    const svg = surface.domSvgContainer;
    if (!svg) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      try {
        const markup = new XMLSerializer().serializeToString(svg);
        const img = new Image();
        img.onload = () => {
          try {
            ctx.drawImage(img, 0, 0, width, height);
          } catch {
            // a tainted or undecodable layer must not lose the chart itself
          }
          resolve();
        };
        img.onerror = () => resolve();
        img.src =
          'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(markup);
      } catch {
        resolve();
      }
    });
  }

  /**
   * Is this capture a flat field of one colour?
   *
   * The failure being guarded is silent: a blank capture is a perfectly valid
   * PNG, so without this check an empty chart reaches the report looking like a
   * measurement that had nothing in it.
   */
  /**
   * Settle when `p` settles, or after `ms`, whichever comes first.
   *
   * THE FAILURE IS A SILENCE, NOT A REJECTION. A promise that never settles is
   * not caught by try/catch and not reported by .catch() — the await simply
   * never returns. All three awaits bounded here are that shape: two Image
   * decodes that may neither load nor error, and a dom-to-image rasterise with
   * no bound. Same family as the two already bounded in ExportPrerenderService.
   *
   * Resolves with `fallback` rather than rejecting, so a stalled decode degrades
   * to "this capture failed" — which the caller already handles by naming the
   * chart and refusing the report — instead of hanging the export with no error,
   * no file and nothing for the operator to read.
   */
  private withDeadline<T>(p: Promise<T>, ms: number, label: string, fallback: T): Promise<T> {
    return new Promise((resolve) => {
      let settled = false;
      const finish = (v: T) => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(v);
      };
      const timer = setTimeout(() => {
        console.warn(`ChartCapture: ${label} exceeded ${ms} ms — treating as failed`);
        finish(fallback);
      }, ms);
      p.then((v) => finish(v), (err) => {
        console.warn(`ChartCapture: ${label} failed`, err);
        finish(fallback);
      });
    });
  }

  private async isBlank(base64: string): Promise<boolean> {
    try {
      const img = new Image();
      img.src = 'data:image/png;base64,' + base64;
      const decoded = await this.withDeadline(
        new Promise<boolean>((res, rej) => {
          img.onload = () => res(true);
          img.onerror = rej;
        }),
        IMAGE_DECODE_TIMEOUT_MS,
        'blank-check image decode',
        false
      );
      if (!decoded) {
        // Cannot read it, so cannot call it blank. Keep the capture rather than
        // discard a possibly good image because a decode stalled.
        return false;
      }
      // SAMPLE THE PLOT AREA ONLY.
      //
      // The previous version sampled the whole image, so the 2D overlay's axis
      // labels, ticks and gridlines made it non-uniform — and it passed a chart
      // whose PLOT was completely empty. That is exactly the reported case: the
      // composite succeeded, this said "not blank", and the report still got an
      // empty box. The axes are real content; they are just not the content
      // being asked about.
      //
      // Crop 10% off each edge: enough to clear the axis gutters, tight enough
      // to keep a trace that hugs the bottom of the plot. A flat cure sitting on
      // its baseline is real data, and a wider crop would throw it away as blank
      // — a worse failure than the one being fixed, because it discards a good
      // capture rather than merely failing to flag a bad one.
      const probe = document.createElement('canvas');
      probe.width = 64;
      probe.height = 32;
      const pctx = probe.getContext('2d');
      if (!pctx) {
        return false;
      }
      const cropX = img.width * 0.1;
      const cropY = img.height * 0.1;
      pctx.drawImage(
        img,
        cropX, cropY, img.width - cropX * 2, img.height - cropY * 2,
        0, 0, probe.width, probe.height
      );
      const { data } = pctx.getImageData(0, 0, probe.width, probe.height);
      const r0 = data[0], g0 = data[1], b0 = data[2];
      for (let i = 4; i < data.length; i += 4) {
        if (
          Math.abs(data[i] - r0) > 6 ||
          Math.abs(data[i + 1] - g0) > 6 ||
          Math.abs(data[i + 2] - b0) > 6
        ) {
          return false;
        }
      }
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Capture a DOM node at a higher-than-1× pixel ratio.
   * dom-to-image renders at 1:1 CSS px, so we first get a 1× image,
   * then paint it scaled onto a larger canvas.
   */
  private async captureNodeHiRes(
    node: HTMLElement,
    cssWidth: number,
    cssHeight: number,
    ratio: number
  ): Promise<string | null> {
    const dataUrl = await this.withDeadline(
      domtoimage.toPng(node, {
        bgcolor: this.reportTheme ? REPORT_BACKGROUND : CAPTURE_BACKGROUND,
        width: cssWidth,
        height: cssHeight,
      }),
      DOM_TO_IMAGE_TIMEOUT_MS,
      'dom-to-image rasterise',
      ''
    );
    if (!dataUrl) {
      return null;
    }

    if (ratio <= 1) {
      return dataUrl.split(',')[1] || null;
    }

    // Scale up to final print resolution
    const img = new Image();
    img.src = dataUrl;
    const scaled = await this.withDeadline(
      new Promise<boolean>((resolve, reject) => {
        img.onload = () => resolve(true);
        img.onerror = reject;
      }),
      IMAGE_DECODE_TIMEOUT_MS,
      'hi-res scale-up decode',
      false
    );
    if (!scaled) {
      return null;
    }

    const canvas = document.createElement('canvas');
    canvas.width = cssWidth * ratio;
    canvas.height = cssHeight * ratio;
    const ctx = canvas.getContext('2d')!;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const hiResUrl = canvas.toDataURL('image/png');
    return hiResUrl.split(',')[1] || null;
  }

}
